import { serverDate } from '../../../shared/helpers/dates';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { InvoicesService } from '../../../shared/services/invoices.service';
import { TransactionsService } from '../../../shared/services/transactions.service';
import {
  INVOICE_STATUS_META,
  Invoice,
} from '../../../shared/types/invoice.types';
import {
  TRANSACTION_STATUS_META,
  Transaction,
} from '../../../shared/types/transaction.types';

/** Payment method options proposed in the "mark as paid" form. */
const PAYMENT_METHOD_OPTIONS: PrettySelectOption<string>[] = [
  { value: 'CASH', label: 'Espèces' },
  { value: 'WAVE', label: 'Wave' },
  { value: 'ORANGE_MONEY', label: 'Orange Money' },
  { value: 'FREE_MONEY', label: 'Free Money' },
  { value: 'BANK_TRANSFER', label: 'Virement bancaire' },
  { value: 'CARD', label: 'Carte bancaire' },
  { value: 'OTHER', label: 'Autre' },
];

@Component({
  selector: 'app-invoice-detail',
  imports: [
    CommonModule,
    RouterModule,
    // FormsModule : les forms « Marquer payée » / « Annuler » utilisent des
    // `[formControl]` standalone dans un `<form (ngSubmit)>` sans `[formGroup]`.
    // Sans FormsModule, `NgForm` ne s'attache pas → submit natif = reload de page
    // et submitPay()/submitCancel() ne s'exécutent jamais. Cf. fix 2026-07-17.
    FormsModule,
    ReactiveFormsModule,
    LocalizedDatePipe,
    DecimalPipe,
    PageHeaderComponent,
    DataStateComponent,
    ModalComponent,
    PrettySelectComponent,
    UserCellComponent,
  ],
  templateUrl: './invoice-detail.component.html',
})
export class InvoiceDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly invoicesService = inject(InvoicesService);
  private readonly transactionsService = inject(TransactionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly invoice = signal<Invoice | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  /** Linked payment transactions (lazy-loaded after invoice fetch). */
  readonly linkedTxs = signal<Transaction[]>([]);
  readonly linkedTxsLoading = signal(false);
  readonly txStatusMeta = TRANSACTION_STATUS_META;

  // Mark-as-paid modal
  readonly payOpen = signal(false);
  readonly paying = signal(false);
  readonly payError = signal<string | null>(null);
  readonly paymentMethodCtrl = new FormControl<string>('CASH', {
    nonNullable: true,
    validators: [Validators.required],
  });
  readonly paymentReferenceCtrl = new FormControl<string>('', { nonNullable: true });
  readonly paymentMethodOptions = PAYMENT_METHOD_OPTIONS;

  // Cancel modal
  readonly cancelOpen = signal(false);
  readonly cancelling = signal(false);
  readonly cancelError = signal<string | null>(null);
  readonly cancelReasonCtrl = new FormControl<string>('', { nonNullable: true });

  readonly statusMeta = INVOICE_STATUS_META;

  /** Whether the "mark as paid" CTA should be visible. */
  readonly canMarkPaid = computed(() => {
    const s = this.invoice()?.status;
    return s === 'PENDING' || s === 'OVERDUE';
  });

  /** Whether the "cancel" CTA should be visible. */
  readonly canCancel = computed(() => {
    const s = this.invoice()?.status;
    return s === 'PENDING' || s === 'OVERDUE';
  });

  /** True when the invoice is past due and unpaid. */
  readonly isOverdue = computed(() => {
    const inv = this.invoice();
    if (!inv?.dueDate) return false;
    if (inv.status === 'PAID' || inv.status === 'CANCELLED' || inv.status === 'REFUNDED') return false;
    return serverDate(inv.dueDate).getTime() < Date.now();
  });

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.invoicesService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inv) => {
          this.invoice.set(inv);
          this.loading.set(false);
          this.fetchLinkedTxs();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cette facture.'),
          );
        },
      });
  }

  /** Best-effort: list payment transactions emitted for this invoice. */
  private fetchLinkedTxs(): void {
    this.linkedTxsLoading.set(true);
    this.transactionsService
      .bySource('rental', String(this.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (txs) => {
          // Sort most-recent first (createdAt desc).
          const sorted = [...(txs ?? [])].sort((a, b) => {
            const ta = a.createdAt ? serverDate(a.createdAt).getTime() : 0;
            const tb = b.createdAt ? serverDate(b.createdAt).getTime() : 0;
            return tb - ta;
          });
          this.linkedTxs.set(sorted);
          this.linkedTxsLoading.set(false);
        },
        error: () => {
          this.linkedTxs.set([]);
          this.linkedTxsLoading.set(false);
        },
      });
  }

  // ── Mark as paid ─────────────────────────────────────────────────────
  openPay(): void {
    this.payError.set(null);
    this.paymentMethodCtrl.reset('CASH');
    this.paymentReferenceCtrl.reset('');
    this.payOpen.set(true);
  }
  closePay(): void {
    if (this.paying()) return;
    this.payOpen.set(false);
  }
  submitPay(): void {
    if (this.paymentMethodCtrl.invalid) {
      this.paymentMethodCtrl.markAsTouched();
      return;
    }
    this.paying.set(true);
    this.payError.set(null);
    this.invoicesService
      .markAsPaid(this.id, {
        paymentMethod: this.paymentMethodCtrl.value,
        paymentReference: this.paymentReferenceCtrl.value || undefined,
      })
      .subscribe({
        next: (inv) => {
          this.paying.set(false);
          this.payOpen.set(false);
          this.invoice.set(inv);
        },
        error: (err: unknown) => {
          this.paying.set(false);
          this.payError.set(toErrorMessage(err, 'Échec du marquage comme payée.'));
        },
      });
  }

  // ── Cancel ───────────────────────────────────────────────────────────
  openCancel(): void {
    this.cancelError.set(null);
    this.cancelReasonCtrl.reset('');
    this.cancelOpen.set(true);
  }
  closeCancel(): void {
    if (this.cancelling()) return;
    this.cancelOpen.set(false);
  }
  submitCancel(): void {
    this.cancelling.set(true);
    this.cancelError.set(null);
    this.invoicesService
      .cancel(this.id, this.cancelReasonCtrl.value || undefined)
      .subscribe({
        next: (inv) => {
          this.cancelling.set(false);
          this.cancelOpen.set(false);
          this.invoice.set(inv);
        },
        error: (err: unknown) => {
          this.cancelling.set(false);
          this.cancelError.set(toErrorMessage(err, "L'annulation a échoué."));
        },
      });
  }
}
